import type { ViewMode } from '@/types/calendar'

export function isWeekend(dayOfWeek: string): boolean {
  return dayOfWeek === 'Sat' || dayOfWeek === 'Sun'
}

function localIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * Today's date as a `YYYY-MM-DD` string, on the clock that owns the question.
 *
 * **Pass the property's timezone whenever a STAY DATE is being classified.** A
 * stay date belongs to the hotel, not to whoever is looking at it: 25 July is
 * still today for a London property until 23:59 London, even though an operator
 * in Cairo has been on the 26th for two hours. Reading the viewer's clock told
 * that operator a night they could still reprice was in the past and disabled
 * Edit Prices on it — the calendar has to answer "is this date still live?" the
 * way the hotel would, which is also how the backend answers it (`Date.current`
 * under `config.time_zone`).
 *
 * Omit the zone only for things that are genuinely about the VIEWER's now — a
 * greeting, "last synced 3m ago". Those are not stay dates.
 *
 * Never `toISOString()`, with or without a zone: that formats in UTC, which is a
 * third clock again and names the wrong calendar day inside the offset window.
 */
export function todayIso(timeZone?: string | null): string {
  const now = new Date()
  if (!timeZone) return localIso(now)

  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now)
    const at = (type: string) => parts.find((p) => p.type === type)?.value
    const [year, month, day] = [at('year'), at('month'), at('day')]
    return year && month && day ? `${year}-${month}-${day}` : localIso(now)
  } catch {
    // An unrecognised zone must never break the calendar — fall back to the
    // viewer's clock, which is what this did before it knew about zones.
    return localIso(now)
  }
}

export function isToday(dateStr: string, timeZone?: string | null): boolean {
  return dateStr === todayIso(timeZone)
}

/** True when `dateStr` (YYYY-MM-DD) is strictly before today at `timeZone`. */
export function isPastDate(dateStr: string, timeZone?: string | null): boolean {
  return dateStr < todayIso(timeZone)
}

/**
 * Whole nights from `today` to `dateStr` — negative in the past, 0 for tonight.
 * Both arguments are `YYYY-MM-DD`, so the caller has already decided whose clock
 * "today" came from and every surface reading it agrees.
 */
export function nightsUntil(dateStr: string, today: string): number {
  const ms = new Date(`${dateStr}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()
  return Math.round(ms / 86_400_000)
}

export function currencySymbol(currency: string): string {
  const symbols: Record<string, string> = { GBP: '\u00a3', EUR: '\u20ac', USD: '$' }
  return symbols[currency] || currency + ' '
}

/**
 * Format a rate amount for display. The backend sends already-rounded values —
 * this function MUST NOT round, floor, or modify the number.
 * Integers display as integers, decimals with up to 2dp.
 */
export function formatRate(amount: number, currency: string): string {
  const sym = currencySymbol(currency)
  return Number.isInteger(amount) ? `${sym}${amount}` : `${sym}${amount.toFixed(2)}`
}

/**
 * Format a rate amount using a pre-resolved currency symbol.
 * Same rules as formatRate — integers as integers, decimals with 2dp.
 */
export function formatRateSym(amount: number, sym: string): string {
  return Number.isInteger(amount) ? `${sym}${amount}` : `${sym}${amount.toFixed(2)}`
}

export function occupancyColor(pct: number): string {
  if (pct >= 80) return 'text-emerald-700 bg-emerald-50/80'
  if (pct >= 60) return 'text-amber-700 bg-amber-50/80'
  return 'text-slate-600 bg-slate-50/50'
}

export function buildMonthWeeks(year: number, month: number): (string | null)[][] {
  const firstDay = new Date(year, month - 1, 1)
  const lastDay = new Date(year, month, 0)
  const daysInMonth = lastDay.getDate()
  const startDow = (firstDay.getDay() + 6) % 7

  const weeks: (string | null)[][] = []
  let currentWeek: (string | null)[] = new Array(startDow).fill(null)

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    currentWeek.push(dateStr)
    if (currentWeek.length === 7) {
      weeks.push(currentWeek)
      currentWeek = []
    }
  }

  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null)
    weeks.push(currentWeek)
  }

  return weeks
}

const STORAGE_KEY_VIEW = 'crown-calendar-view'

export function getStoredViewMode(): ViewMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY_VIEW)
    if (v === 'grid' || v === 'calendar' || v === 'timeline') return v
  } catch {}
  return 'calendar'
}

export function setStoredViewMode(mode: ViewMode): void {
  try { localStorage.setItem(STORAGE_KEY_VIEW, mode) } catch {}
}
